/**
 * @file View-as (test mode only) — preview Gear as a different role.
 *
 * Admins in test mode can flip between three perspectives from the test
 * banner: Admin (the real thing), User (a regular employee: no admin nav,
 * bounced to /me), and Office Management (a role that EXISTS but whose
 * permissions are still TBD — until they're defined it renders exactly like
 * User). The override is pure presentation: Firestore still sees the real
 * admin token (rules unchanged), and the flag only ever APPLIES while test
 * mode is on — `getViewAs()` hard-returns 'admin' in production mode, so a
 * stale localStorage value can never demote a working admin.
 *
 * User/Office views borrow a seeded persona identity for employee-scoped
 * pages (/me reads the persona's employee_view + device_view rows instead
 * of the real admin's). Personas are seeded by functions/seed-test-data.js —
 * keep the emails below aligned with the seed fixtures.
 */

import { isTestMode } from './test-mode.js';

export const VIEW_AS_ROLES = [
  { key: 'admin',  label: 'Admin' },
  { key: 'user',   label: 'User' },
  { key: 'office', label: 'Office Management' }, // permissions TBD — renders as User for now
];

// Seeded personas (see functions/seed-test-data.js — keep aligned).
export const VIEW_AS_PERSONAS = {
  user:   { email: 'test.alice@strv.com', name: 'Alice Tester' },
  office: { email: 'test.olga@strv.com',  name: 'Olga Office'  },
};

const LS_KEY = 'gear.viewAs';
const VALID = new Set(VIEW_AS_ROLES.map((r) => r.key));
const listeners = new Set();

let role = readInitial();

function readInitial() {
  try {
    const v = globalThis.localStorage?.getItem(LS_KEY);
    return VALID.has(v) ? v : 'admin';
  } catch {
    return 'admin';
  }
}

/** The active view-as role. Always 'admin' outside test mode. */
export function getViewAs() {
  return isTestMode() ? role : 'admin';
}

/** Flip the view-as role. Persists + notifies (main.js re-mounts the app). */
export function setViewAs(next) {
  if (!VALID.has(next)) return;
  role = next;
  try { globalThis.localStorage?.setItem(LS_KEY, next); } catch { /* in-memory only */ }
  for (const cb of listeners) {
    try { cb(role); } catch (err) { console.error('[view-as] listener error', err); }
  }
}

export function onViewAsChange(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** False while simulating User / Office Management in test mode. */
export function isViewingAsAdmin() {
  return getViewAs() === 'admin';
}

/**
 * The persona email employee-scoped pages (/me) should render for, or null
 * to use the real signed-in user. Non-null only in test mode with a
 * non-admin view-as role.
 */
export function viewAsPersonaEmail() {
  const r = getViewAs();
  return r === 'admin' ? null : (VIEW_AS_PERSONAS[r]?.email || null);
}
